import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EmptySpecialPackageReservationDto,
  IReservationResponseDto,
  ISpecialPackageDto,
  ISpecialPackageReservationDto,
  specialPackageReservationDtoValidation,
} from '../model';
import { IGaItemDto, IGaPurchaseEventDto } from '../model/analytics';
import {
  getGaUserIdFromEmail,
  getHashedPhoneNumber,
  gtmEvent,
} from '../utility';
import { MapAllToAnalyticsItem } from '../mapping';
import { INotificationService, SpecialPackageApi } from '../services';
import PaymentPicker from '../offer-package/payment-picker';
import {
  FormGroup,
  StyledButton,
  StyledCheckButton,
  StyledInput,
  StyledTextarea,
} from '../ui';
import SpecialPackageOccupancyPicker from './occupancy-picker';
import {
  getAvailableSpecialPackageOccupancies,
  getDefaultSpecialPackageOccupancy,
  getSpecialPackagePriceForOccupancy,
  getSpecialPackageRoomPriceForOccupancy,
  isSpecialPackageRoomSelectable,
  SpecialPackageOccupancy,
} from './occupancy';
import SpecialPackageCard from './package-card';
import { getSpecialPackageTitle } from './text';
import { ISpecialPackageReferenceData } from './use-special-package-references';

export interface ISpecialPackageFormProps {
  specialPackage: ISpecialPackageDto;
  references: ISpecialPackageReferenceData;
  notificationService: INotificationService;
  initialOccupancy?: number | null;
}

export function SpecialPackageForm(props: ISpecialPackageFormProps) {
  const { t, i18n } = useTranslation('offer-package');
  const { specialPackage, references } = props;
  const availableOccupancies =
    getAvailableSpecialPackageOccupancies(specialPackage);
  const specialPackageTitle = getSpecialPackageTitle(
    specialPackage,
    i18n.language
  );
  const defaultOccupancy = availableOccupancies.includes(
    props.initialOccupancy as SpecialPackageOccupancy
  )
    ? (props.initialOccupancy as SpecialPackageOccupancy)
    : getDefaultSpecialPackageOccupancy(specialPackage);
  const initialRooms = specialPackage.specialPackageRoomCategoryList.filter(
    (room) => isSpecialPackageRoomSelectable(room, defaultOccupancy)
  );

  const methods = useForm<ISpecialPackageReservationDto>({
    resolver: yupResolver(specialPackageReservationDtoValidation(t)),
    defaultValues: {
      ...EmptySpecialPackageReservationDto,
      contactLanguage: i18n.language,
      occupancy: defaultOccupancy,
      specialPackageRoomCategoryId:
        initialRooms.length === 1 ? initialRooms[0].id : null,
    },
    mode: 'onChange',
  });

  const specialPackageApi = new SpecialPackageApi();
  const [response, setResponse] = useState<IReservationResponseDto>();
  const selectedOccupancy = methods.watch('occupancy');
  const normalizedOccupancy = availableOccupancies.includes(
    selectedOccupancy as SpecialPackageOccupancy
  )
    ? (selectedOccupancy as SpecialPackageOccupancy)
    : defaultOccupancy;
  const visibleRooms = specialPackage.specialPackageRoomCategoryList.filter(
    (room) => isSpecialPackageRoomSelectable(room, normalizedOccupancy)
  );

  useEffect(() => {
    if (selectedOccupancy !== normalizedOccupancy) {
      methods.setValue('occupancy', normalizedOccupancy, {
        shouldValidate: true,
      });
    }
  }, [methods, normalizedOccupancy, selectedOccupancy]);

  useEffect(() => {
    const selectedRoomCategoryId = methods.getValues(
      'specialPackageRoomCategoryId'
    );
    const hasSelectedRoom = visibleRooms.some(
      (room) => room.id === selectedRoomCategoryId
    );
    const nextRoomCategoryId =
      visibleRooms.length === 1 ? visibleRooms[0].id : null;

    if (hasSelectedRoom || selectedRoomCategoryId === nextRoomCategoryId) {
      return;
    }

    methods.setValue('specialPackageRoomCategoryId', nextRoomCategoryId, {
      shouldValidate: true,
    });
  }, [methods, normalizedOccupancy, visibleRooms]);

  const getRoomTitle = (id: number | null) => {
    if (id === null) {
      return t('special.shared.room');
    }

    const room = references.roomCategoryById[id];

    if (!room) {
      return `${t('special.shared.room')} #${id}`;
    }

    return (
      room.roomCategoryTranslationList.find(
        (translation) => translation.langCode === i18n.language
      )?.title ?? room.internalName
    );
  };

  const renderOriginalPrice = (price: number | null) => {
    if (price === null) {
      return null;
    }

    return (
      <div className="text-right">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          {t('special.shared.original-price')}
        </p>
        <p className="font-semibold text-slate-900">
          {price.toFixed(2).replace('.', ',')}€
        </p>
      </div>
    );
  };

  const trackPurchase = async (
    dto: ISpecialPackageReservationDto,
    result: IReservationResponseDto
  ) => {
    const packagePrice = getSpecialPackagePriceForOccupancy(
      specialPackage,
      dto.occupancy
    );
    const selectedRoom = specialPackage.specialPackageRoomCategoryList.find(
      (item) => item.id === dto.specialPackageRoomCategoryId
    );

    const items: IGaItemDto[] = [];

    if (selectedRoom) {
      const roomPrice = getSpecialPackageRoomPriceForOccupancy(
        selectedRoom,
        dto.occupancy
      );
      const room =
        selectedRoom.roomCategoryId === null
          ? undefined
          : references.roomCategoryById[selectedRoom.roomCategoryId];

      if (room && roomPrice !== null) {
        items.push(
          MapAllToAnalyticsItem(
            {
              ...room,
              id: `room_${room.id}`,
            },
            roomPrice,
            1,
            specialPackageTitle
          )
        );
      }
    }

    for (const item of specialPackage.specialPackageCureList) {
      const cure = references.cureById[item.cureId];

      if (cure && item.price !== null) {
        items.push(
          MapAllToAnalyticsItem(
            {
              ...cure,
              id: `cure_${cure.id}`,
            },
            item.price,
            item.amount,
            specialPackageTitle
          )
        );
      }
    }

    for (const item of specialPackage.specialPackageTreatmentList) {
      const treatment = references.treatmentById[item.treatmentId];

      if (treatment && item.price !== null) {
        items.push(
          MapAllToAnalyticsItem(
            {
              ...treatment,
              id: `treatment_${treatment.id}`,
            },
            item.price,
            item.amount,
            specialPackageTitle
          )
        );
      }
    }

    for (const item of specialPackage.specialPackageProductList) {
      const product = references.productById[item.productId];

      if (product && item.price !== null) {
        items.push(
          MapAllToAnalyticsItem(
            {
              ...product,
              id: `product_${product.id}`,
            },
            item.price,
            item.amount,
            specialPackageTitle
          )
        );
      }
    }

    const payload: IGaPurchaseEventDto = {
      currency: 'EUR',
      value: packagePrice ?? 0,
      transaction_id: `special_package_${result.reservationId ?? 'unknown'}`,
      items,
    };

    gtmEvent('purchase', await getGaUserIdFromEmail(dto.contactMail), {
      ...payload,
      phone: getHashedPhoneNumber(dto.contactPhone),
    });
  };

  const onSave = async (dto: ISpecialPackageReservationDto): Promise<void> => {
    try {
      const result = await specialPackageApi.buy(specialPackage.id, dto);

      if (!result.hasError && !result.hasErrorPayment) {
        await trackPurchase(dto, result);
      }

      setResponse(result);
    } catch {
      props.notificationService.createNotification({
        title: 'ERROR',
        type: 'error',
        description: 'CALL REZEPTION',
        hideAfterSeconds: 10,
      });
    }
  };

  if (response) {
    return (
      <div className="mt-4 mb-10">
        <PaymentPicker response={response}></PaymentPicker>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="mt-4 mb-32">
        <form
          onSubmit={methods.handleSubmit((data) => {
            return onSave(data).then(() => methods.reset(data));
          })}
          className="space-y-4"
        >
          <FormGroup
            title={t('special.booking.occupancy-title')}
            description={t('special.booking.occupancy-description')}
          >
            <div className="sm:col-span-6 space-y-3">
              <Controller
                name="occupancy"
                control={methods.control}
                render={(field) => (
                  <SpecialPackageOccupancyPicker
                    availableOccupancies={availableOccupancies}
                    selectedOccupancy={field.field.value}
                    onChange={field.field.onChange}
                  ></SpecialPackageOccupancyPicker>
                )}
              />

              {availableOccupancies.length === 1 && (
                <div className="rounded-lg bg-slate-50 px-4 py-3 text-slate-900">
                  {t(
                    normalizedOccupancy === 1
                      ? 'special.shared.one-person'
                      : 'special.shared.two-persons'
                  )}
                </div>
              )}

              {methods.formState.errors.occupancy?.message && (
                <p className="text-sm text-red-700">
                  {methods.formState.errors.occupancy.message}
                </p>
              )}
            </div>
          </FormGroup>

          <FormGroup
            title={t('special.booking.room-title')}
            description={t('special.booking.room-description')}
          >
            <div className="sm:col-span-6 grid gap-3">
              <Controller
                name="specialPackageRoomCategoryId"
                control={methods.control}
                render={(field) => (
                  <>
                    {visibleRooms.length === 0 && (
                      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                        {t('special.booking.no-rooms-for-occupancy')}
                      </div>
                    )}

                    {visibleRooms.map((room) => {
                      const isSelected = field.field.value === room.id;
                      const roomPrice = getSpecialPackageRoomPriceForOccupancy(
                        room,
                        normalizedOccupancy
                      );

                      return (
                        <StyledCheckButton
                          key={room.id}
                          type="button"
                          isChecked={isSelected}
                          onClick={() => field.field.onChange(room.id)}
                          className={classNames(
                            'items-start',
                            isSelected &&
                              '!bg-brand-green-700 !text-white !ring-brand-green-700 outline-brand-green-700'
                          )}
                        >
                          <div className="flex w-full items-start justify-between gap-4 text-left">
                            <div className="space-y-1">
                              <p
                                className={classNames(
                                  'font-semibold',
                                  isSelected ? 'text-white' : 'text-slate-900'
                                )}
                              >
                                {getRoomTitle(room.roomCategoryId)}
                              </p>
                              <p
                                className={classNames(
                                  'text-sm',
                                  isSelected ? 'text-white/90' : 'opacity-80'
                                )}
                              >
                                {t(
                                  normalizedOccupancy === 1
                                    ? 'special.shared.one-person'
                                    : 'special.shared.two-persons'
                                )}
                              </p>
                            </div>

                            <div
                              className={classNames(
                                isSelected &&
                                  '[&_p]:!text-white/80 [&_div]:!text-white'
                              )}
                            >
                              {renderOriginalPrice(roomPrice)}
                            </div>
                          </div>
                        </StyledCheckButton>
                      );
                    })}
                  </>
                )}
              />

              {methods.formState.errors.specialPackageRoomCategoryId
                ?.message && (
                <p className="text-sm text-red-700">
                  {
                    methods.formState.errors.specialPackageRoomCategoryId
                      .message
                  }
                </p>
              )}
            </div>
          </FormGroup>

          <FormGroup title={t('contact.title')} addColon={true}>
            <div className="sm:col-span-3">
              <Controller
                name="contactMail"
                control={methods.control}
                render={(s) =>
                  StyledInput({
                    ...s,
                    title: t('contact.mail'),
                    prefix: '✉',
                    inputType: 'email',
                  })
                }
              />
            </div>

            <div className="sm:col-span-3">
              <Controller
                name="contactPhone"
                control={methods.control}
                render={(s) =>
                  StyledInput({
                    ...s,
                    title: t('contact.call'),
                    prefix: '📞',
                    placeholder: '+43',
                  })
                }
              />
            </div>
          </FormGroup>

          <FormGroup title={t('newsletter.title')}>
            <div className="sm:col-span-6">
              <Controller
                name="newsletter"
                control={methods.control}
                render={(s) => (
                  <button
                    type="button"
                    className={classNames(
                      'rounded-md border p-2 w-full text-left flex gap-2',
                      s.field.value &&
                        'text-green-800 border-green-800 bg-green-50',
                      !s.field.value &&
                        'text-gray-900 border-gray-300 bg-gray-50'
                    )}
                    onClick={() => s.field.onChange(!s.field.value)}
                  >
                    {s.field.value ? ' ☑  ' : ' □  '}
                    {t('newsletter.message')}
                  </button>
                )}
              />
            </div>
          </FormGroup>

          <FormGroup title={t('personal.title')} addColon={true}>
            <div className="sm:col-span-6 grid sm:grid-cols-6">
              <div className="sm:col-span-2">
                <Controller
                  name="contactTitle"
                  control={methods.control}
                  render={(s) =>
                    StyledInput({
                      ...s,
                      title: t('personal.salutation'),
                      hint: '(optional)',
                    })
                  }
                />
              </div>
            </div>

            <div className="sm:col-span-3 lg:col-span-2">
              <Controller
                name="contactFirstname"
                control={methods.control}
                render={(s) =>
                  StyledInput({ ...s, title: t('personal.firstname') })
                }
              />
            </div>

            <div className="sm:col-span-3 lg:col-span-2">
              <Controller
                name="contactLastname"
                control={methods.control}
                render={(s) =>
                  StyledInput({ ...s, title: t('personal.lastname') })
                }
              />
            </div>

            <div className="sm:col-span-3">
              <Controller
                name="contactBirthday"
                control={methods.control}
                render={(s) =>
                  StyledInput({
                    ...s,
                    title: t('personal.birthday'),
                    inputType: 'date',
                    prefix: '🎂',
                  })
                }
              />
            </div>

            <div className="sm:col-span-6 grid sm:grid-cols-6 gap-x-6 gap-y-8">
              <div className="sm:col-span-3 lg:col-span-2">
                <Controller
                  name="contactStreet"
                  control={methods.control}
                  render={(s) =>
                    StyledInput({
                      ...s,
                      title: t('personal.street'),
                    })
                  }
                />
              </div>

              <div className="sm:col-span-2 lg:col-span-1">
                <Controller
                  name="contactStreetNr"
                  control={methods.control}
                  render={(s) =>
                    StyledInput({
                      ...s,
                      title: t('personal.number'),
                    })
                  }
                />
              </div>
            </div>

            <div className="sm:col-span-6 grid sm:grid-cols-6 gap-x-6 gap-y-8">
              <div className="sm:col-span-2 lg:col-span-1">
                <Controller
                  name="contactPostal"
                  control={methods.control}
                  render={(s) =>
                    StyledInput({
                      ...s,
                      title: t('personal.postal'),
                    })
                  }
                />
              </div>

              <div className="sm:col-span-3 lg:col-span-2">
                <Controller
                  name="contactCity"
                  control={methods.control}
                  render={(s) =>
                    StyledInput({
                      ...s,
                      title: t('personal.city'),
                    })
                  }
                />
              </div>
            </div>

            <div className="sm:col-span-4 lg:col-span-3">
              <Controller
                name="contactCountry"
                control={methods.control}
                render={(s) =>
                  StyledInput({
                    ...s,
                    title: t('personal.country'),
                  })
                }
              />
            </div>
          </FormGroup>

          <FormGroup
            title={t('message.title')}
            isOptional={true}
            description={t('message.description')}
          >
            <div className="sm:col-span-6">
              <Controller
                name="message"
                control={methods.control}
                render={(s) => StyledTextarea({ ...s, title: '' })}
              />
            </div>
          </FormGroup>

          <FormGroup title={t('summary.title')}>
            <div className="sm:col-span-6">
              <SpecialPackageCard
                specialPackage={specialPackage}
                references={references}
                selectedRoomCategoryId={methods.watch(
                  'specialPackageRoomCategoryId'
                )}
                selectedOccupancy={normalizedOccupancy}
              ></SpecialPackageCard>
            </div>

            <div className="sm:col-span-6">
              <Controller
                name="agbAccepted"
                control={methods.control}
                render={(s) => (
                  <button
                    type="button"
                    className={classNames(
                      'rounded-md border p-2 w-full text-left flex gap-2',
                      s.field.value &&
                        'text-green-800 border-green-800 bg-green-50',
                      !s.field.value &&
                        'text-gray-900 border-gray-300 bg-gray-50'
                    )}
                    onClick={() => s.field.onChange(!s.field.value)}
                  >
                    {s.field.value ? ' ☑  ' : ' □  '}
                    {t('agb.message')}
                  </button>
                )}
              />
            </div>

            <div className="sm:col-span-6">
              <Controller
                name="privacyPolicyAccepted"
                control={methods.control}
                render={(s) => (
                  <button
                    type="button"
                    className={classNames(
                      'rounded-md border p-2 w-full text-left flex gap-2',
                      s.field.value &&
                        'text-green-800 border-green-800 bg-green-50',
                      !s.field.value &&
                        'text-gray-900 border-gray-300 bg-gray-50'
                    )}
                    onClick={() => s.field.onChange(!s.field.value)}
                  >
                    {s.field.value ? ' ☑  ' : ' □  '}
                    {t('privacy-policy.message')}
                  </button>
                )}
              />
            </div>

            <div className="sm:col-span-6">
              <StyledButton
                type="submit"
                disabled={
                  !methods.formState.isValid || methods.formState.isSubmitting
                }
                design="primary"
                pulse={methods.formState.isValid}
              >
                {t('shared.book')}
              </StyledButton>
            </div>

            <span className="sm:col-span-6">
              <ul className="list list-inside list-disc">
                <li>{t('price.arrival')}</li>
                <li>{t('price.departure')}</li>
                <li>{t('price.notes')}</li>
                <li>{t('price.storno')}</li>
              </ul>
            </span>

            <div className="sm:col-span-6 flex flex-col gap-1">
              <span>
                {'* '}
                <a
                  href={
                    i18n.language === 'de'
                      ? 'https://www.wko.at/oe/tourismus-freizeitwirtschaft/hotellerie/agb-hotellerie.pdf'
                      : 'https://www.wko.at/oe/tourismus-freizeitwirtschaft/hotellerie/agbh-englisch.pdf'
                  }
                  className="underline"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  {t('agb.title')}
                </a>
              </span>
              <span>
                {'** '}
                <a
                  href="https://www.mandira-ayurveda.at/datenschutz/"
                  className="underline"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  {t('privacy-policy.title')}
                </a>
              </span>
            </div>
          </FormGroup>
        </form>
      </div>
    </FormProvider>
  );
}

export default SpecialPackageForm;
